import { bodyPath } from './butterfly-art';
import { ART_HEIGHT, ART_WIDTH, WING_SHAPES } from './layout';
import type { ButterflySpecies } from './species';

const readyCanvasCache = new Map<string, HTMLCanvasElement>();
const readyDataUrlCache = new Map<string, string>();

function ellipse(g: CanvasRenderingContext2D, x: number, y: number, rx: number, ry: number, color: string) {
  g.fillStyle = color;
  g.beginPath(); g.ellipse(x, y, rx, ry, -0.3, 0, Math.PI * 2); g.fill();
}

function paintSide(g: CanvasRenderingContext2D, species: ButterflySpecies) {
  const shape = WING_SHAPES[species.shape];
  const fore = new Path2D(shape.fore), hind = new Path2D(shape.hind);
  const union = new Path2D(); union.addPath(fore); union.addPath(hind);
  for (const [path, color] of [[fore, species.color], [hind, species.hindColor]] as const) {
    const gradient = g.createLinearGradient(480, 260, 100, 120);
    gradient.addColorStop(0, species.edge);
    gradient.addColorStop(0.28, color);
    if (species.pattern === 'sheen') gradient.addColorStop(0.65, species.accent);
    if (species.pattern === 'rainbow') {
      gradient.addColorStop(0.45, '#64d6bd');
      gradient.addColorStop(0.6, '#e9d865');
      gradient.addColorStop(0.75, '#f58a79');
      gradient.addColorStop(0.9, species.accent);
    }
    gradient.addColorStop(1, color);
    g.globalAlpha = species.pattern === 'glass' ? 0.22 : 1;
    g.fillStyle = gradient; g.fill(path);
  }
  g.globalAlpha = 1;
  g.save(); g.clip(union);
  g.strokeStyle = species.edge;
  g.lineWidth = species.pattern === 'veins' || species.pattern === 'bands' ? 38 : 16;
  g.lineJoin = 'round'; g.stroke(fore); g.stroke(hind);

  g.lineWidth = species.pattern === 'veins' ? 9 : 3;
  g.globalAlpha = species.pattern === 'veins' ? 1 : 0.32;
  for (const [x, y] of [[100, 65], [150, 125], [130, 180], [210, 230], [200, 345], [260, 435], [345, 410]]) {
    g.beginPath(); g.moveTo(489, 259);
    g.quadraticCurveTo((489 + x) / 2, (259 + y) / 2 - 28, x, y); g.stroke();
  }
  g.globalAlpha = 1;

  if (species.pattern === 'eyes' || species.pattern === 'moon') {
    const points = species.pattern === 'eyes' ? [[180, 140, 48], [...shape.eye, 39]] : [[215, 160, 23], [...shape.eye, 20]];
    for (const [x, y, radius] of points) {
      ellipse(g, x, y, radius, radius * 0.85, species.accent);
      ellipse(g, x, y, radius * 0.76, radius * 0.67, species.edge);
      ellipse(g, x, y, radius * 0.5, radius * 0.44, species.pattern === 'eyes' ? '#5288d8' : '#d6eea9');
      ellipse(g, x + 5, y - 5, radius * 0.19, radius * 0.17, '#fff9dc');
    }
  } else if (species.pattern === 'bands') {
    g.lineWidth = 19;
    for (const x of [150, 235, 320, 405]) {
      g.beginPath(); g.moveTo(x - 60, 25); g.lineTo(x + 48, 245); g.stroke();
    }
    for (const [x, y] of [[225, 360], [260, 390], [305, 410], [350, 395]]) ellipse(g, x, y, 18, 10, species.accent);
    ellipse(g, 284, 414, 13, 14, '#ed7652');
  } else if (species.pattern === 'leaf') {
    ellipse(g, 250, 165, 10, 13, species.accent);
    ellipse(g, shape.eye[0], shape.eye[1], 8, 11, species.accent);
  } else if (species.pattern === 'sheen') {
    g.strokeStyle = species.accent; g.lineWidth = 7; g.globalAlpha = 0.55;
    for (let y = 95; y < 440; y += 18) {
      g.beginPath(); g.moveTo(110, y); g.quadraticCurveTo(290, y + 25, 455, 259); g.stroke();
    }
    g.globalAlpha = 1;
  } else if (species.pattern === 'tips') {
    ellipse(g, 158, 104, 68, 66, species.accent);
    ellipse(g, 285, 185, 15, 19, species.accent);
    ellipse(g, 355, 210, 12, 16, species.accent);
  } else if (species.pattern === 'patches') {
    for (const [x, y] of [[150, 105], [230, 120], [310, 150]]) ellipse(g, x, y, 25, 33, species.edge);
    ellipse(g, 185, 110, 17, 24, '#fff0bb');
    for (const [x, y] of [[200, 340], [235, 390], [280, 414], [330, 405], [370, 380]]) {
      ellipse(g, x, y, 16, 18, species.edge);
      ellipse(g, x, y, 9, 10, species.accent);
    }
  } else if (species.pattern === 'ribbon') {
    g.strokeStyle = species.accent; g.lineWidth = 45;
    g.beginPath(); g.moveTo(255, 45); g.quadraticCurveTo(260, 140, 195, 245); g.stroke();
    g.lineWidth = 28;
    g.beginPath(); g.moveTo(175, 365); g.quadraticCurveTo(265, 470, 405, 380); g.stroke();
    for (const [x, y] of [[120, 85], [155, 120], [175, 160], [225, 125]]) ellipse(g, x, y, 11, 15, '#fff6e1');
  } else if (species.pattern === 'mosaic') {
    ellipse(g, 150, 105, 70, 62, species.edge);
    for (const [x, y] of [[110, 78], [150, 100], [185, 120], [245, 142], [320, 180]]) ellipse(g, x, y, 13, 17, species.accent);
    for (const [x, y] of [[260, 190], [350, 215], [235, 350], [275, 388], [325, 398], [365, 375]]) {
      ellipse(g, x, y, 15, 18, species.edge);
      if (y > 280) ellipse(g, x, y, 6, 8, species.color);
    }
  } else if (species.pattern === 'glass') {
    g.strokeStyle = species.accent; g.lineWidth = 15;
    g.beginPath(); g.moveTo(180, 95); g.quadraticCurveTo(245, 155, 280, 225); g.stroke();
    g.strokeStyle = species.edge; g.lineWidth = 4;
    for (const [x, y] of [[215, 85], [230, 170], [300, 350], [350, 395]]) {
      g.beginPath(); g.moveTo(489, 259); g.lineTo(x, y); g.stroke();
    }
  } else if (species.pattern === 'zebra') {
    g.strokeStyle = species.accent; g.lineWidth = 20;
    for (const x of [95, 170, 245, 320, 395]) {
      g.beginPath(); g.moveTo(x, 35); g.quadraticCurveTo(x + 115, 210, x + 30, 455); g.stroke();
    }
  } else if (species.pattern === 'emerald') {
    for (const [x, y, rx, ry] of [[135, 105, 24, 38], [205, 140, 25, 44], [280, 174, 24, 48], [355, 205, 22, 40]]) {
      ellipse(g, x, y, rx, ry, species.accent);
    }
    for (const [x, y] of [[210, 360], [255, 395], [305, 405], [355, 380]]) ellipse(g, x, y, 13, 17, species.edge);
  } else if (species.pattern === 'freckles') {
    for (const [x, y, r] of [[230, 135, 12], [280, 165, 15], [335, 185, 11], [395, 215, 13], [280, 340, 10], [320, 375, 12], [365, 370, 9]]) {
      ellipse(g, x, y, r, r * 1.15, species.accent);
    }
    g.strokeStyle = species.color; g.lineWidth = 22; g.stroke(hind);
  } else if (species.pattern === 'rainbow') {
    g.strokeStyle = species.edge; g.lineWidth = 12;
    for (const x of [110, 190, 280, 370]) {
      g.beginPath(); g.moveTo(x - 20, 40); g.quadraticCurveTo(x + 90, 190, x + 5, 465); g.stroke();
    }
    for (const [x, y] of [[230, 372], [270, 407], [315, 405], [355, 380]]) ellipse(g, x, y, 8, 11, '#fff5cd');
  }

  if (species.pattern === 'blue' || species.pattern === 'veins') {
    for (const [x, y] of [[145, 100], [185, 104], [228, 120], [275, 142], [320, 169], [365, 195], [200, 355], [220, 400], [260, 425], [305, 414], [345, 389]]) {
      ellipse(g, x, y, species.pattern === 'blue' ? 9 : 6, species.pattern === 'blue' ? 12 : 8, species.accent);
    }
  }
  g.restore();
  g.strokeStyle = species.edge; g.lineWidth = 5; g.lineJoin = 'round';
  g.stroke(fore); g.stroke(hind);
}

/** A symmetric, finished design on the same sheet used by uploaded colourings. */
export function paintReadyArt(species: ButterflySpecies): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = ART_WIDTH; canvas.height = ART_HEIGHT;
  const g = canvas.getContext('2d');
  if (!g) throw new Error('Canvas 2D недоступен');
  paintSide(g, species);
  g.save(); g.translate(ART_WIDTH, 0); g.scale(-1, 1); paintSide(g, species); g.restore();
  const body = bodyPath();
  g.fillStyle = species.edge; g.fill(body);
  g.save(); g.clip(body);
  g.strokeStyle = species.hindColor; g.lineWidth = 5;
  for (let y = 290; y < 460; y += 24) { g.beginPath(); g.moveTo(490, y); g.lineTo(534, y); g.stroke(); }
  g.restore();
  return canvas;
}

/** Reuse ready-made art across the meadow, menu previews, and moderation cards. */
export function readyArtCanvas(species: ButterflySpecies): HTMLCanvasElement {
  let canvas = readyCanvasCache.get(species.id);
  if (!canvas) {
    canvas = paintReadyArt(species);
    readyCanvasCache.set(species.id, canvas);
  }
  return canvas;
}

/** Image source for UI surfaces whose shared component expects an <img>. */
export function readyArtDataUrl(species: ButterflySpecies): string {
  let dataUrl = readyDataUrlCache.get(species.id);
  if (!dataUrl) {
    dataUrl = readyArtCanvas(species).toDataURL('image/png');
    readyDataUrlCache.set(species.id, dataUrl);
  }
  return dataUrl;
}
